import { EnrollmentStatus } from '@sulis-liiga/common';
import type { Enrollment } from '@sulis-liiga/common';

/**
 * A ladder is identified by category + base name, since `divisionGroupId` is
 * regenerated per season — this is the stable key a team's enrollments across
 * several seasons collapse onto.
 */
export function ladderKey(enrollment: Enrollment): string {
  return `${enrollment.category}::${enrollment.divisionBaseName ?? enrollment.divisionGroupId}`;
}

// Lower number = shown in preference over a higher one when a ladder has
// several enrollments (one per season) to collapse into a single row.
const STATUS_PRIORITY: Record<EnrollmentStatus, number> = {
  [EnrollmentStatus.ACTIVE]: 0,
  [EnrollmentStatus.PENDING_PLACEMENT]: 1,
  [EnrollmentStatus.QUEUED]: 2,
  // A finished participation (past season) outranks a withdrawal but is only the
  // representative for a ladder when the team holds no current enrollment there.
  [EnrollmentStatus.COMPLETED]: 3,
  [EnrollmentStatus.WITHDRAWN]: 4,
};

/**
 * A ladder's representative enrollment, enriched with the earliest season the
 * team has held any (season-scoped) enrollment on that ladder — spans the
 * seasons collapsed away by `groupEnrollmentsByLadder`.
 */
export interface GroupedEnrollment extends Enrollment {
  /**
   * The earliest `seasonId` among this ladder-group's enrollments that have
   * one. Undefined when every enrollment in the group is still `queued`
   * (season-less).
   */
  enrolledSinceSeasonId?: string;
}

/**
 * Collapse a team's enrollments — one record per season × ladder — into a
 * single representative row per ladder (grouped by `ladderKey`). Prefers a
 * non-withdrawn enrollment; among those, active > pending_placement > queued.
 * If every enrollment for a ladder is withdrawn, one withdrawn row is kept.
 * Each representative also carries `enrolledSinceSeasonId`, the earliest
 * seasonId among the group's members (undefined if all are queued).
 */
export function groupEnrollmentsByLadder(enrollments: Enrollment[]): GroupedEnrollment[] {
  const groups = new Map<string, Enrollment[]>();
  for (const enrollment of enrollments) {
    const key = ladderKey(enrollment);
    const existing = groups.get(key);
    if (existing) {
      existing.push(enrollment);
    } else {
      groups.set(key, [enrollment]);
    }
  }

  const representatives: GroupedEnrollment[] = [];
  for (const group of Array.from(groups.values())) {
    const [best] = [...group].sort(
      (a, b) => (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99),
    );
    const seasonIds = group
      .map(e => e.seasonId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    const enrolledSinceSeasonId = seasonIds.length > 0 ? seasonIds.sort()[0] : undefined;
    representatives.push({ ...best, enrolledSinceSeasonId });
  }
  return representatives;
}
